import { useGameStore } from "../core/store/gameStore";
import { LoadingScreen } from "./LoadingScreen";
import AudioButton from "./AudioButton";
import { SideBar } from "./SideBar";
import { TouchJoystick } from "../core/input/TouchJoystick";
import { input } from "../core/input/controls";
import { useEffect, useState, useCallback, useMemo } from "react";

// ── 底部快捷鍵提示（從心遊語宙降落時顯示） ──
function ControlHints({ isMobile }: { isMobile: boolean }) {
    const [visible, setVisible] = useState(true);

    // 15 秒後自動淡出（給玩家充足時間閱讀提示）
    useEffect(() => {
        const t = setTimeout(() => setVisible(false), 15000);
        return () => clearTimeout(t);
    }, []);

    const keyStyle: React.CSSProperties = {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: '16px', height: '20px', padding: '0 4px',
        border: '1px solid rgba(255,255,255,0.25)', borderRadius: '3px',
        background: 'rgba(255,255,255,0.06)',
        fontFamily: 'monospace', fontSize: '0.65rem', fontWeight: 'bold',
        color: 'rgba(255,255,255,0.6)', lineHeight: 1, margin: '0 2px',
    };

    return (
        <div style={{
            position: 'fixed', bottom: '16px', left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex', gap: '20px', alignItems: 'center',
            padding: '6px 16px', borderRadius: '8px',
            background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255,255,255,0.06)',
            pointerEvents: 'none', userSelect: 'none',
            opacity: visible ? 0.7 : 0,
            transition: 'opacity 1.5s ease',
            fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)',
            letterSpacing: '0.5px', whiteSpace: 'nowrap',
            zIndex: 100,
        }}>
            {isMobile ? (
                <>
                    <span><span style={keyStyle}>搖桿</span> 移動</span>
                    <span><span style={keyStyle}>觸控</span> 視角</span>
                </>
            ) : (
                <>
                    <span><span style={keyStyle}>W</span><span style={keyStyle}>A</span><span style={keyStyle}>S</span><span style={keyStyle}>D</span> 移動</span>
                    <span><span style={keyStyle}>Shift</span> 奔跑</span>
                    <span><span style={keyStyle}>C</span> 視角</span>
                    <span><span style={keyStyle}>ESC</span> 返回語宙</span>
                </>
            )}
        </div>
    );
}

// ── 離開星球升空動畫 ──
function DepartureOverlay({ starColor }: { starColor: string }) {
    // 星點粒子（向上升起）
    const particles = useMemo(() =>
        Array.from({ length: 60 }, () => ({
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            delay: `${Math.random() * 0.8}s`,
            duration: `${0.7 + Math.random() * 0.8}s`,
            size: 1 + Math.random() * 2,
        })), []);

    // 星軌條紋（長線條向上拉伸，營造速度感）
    const trails = useMemo(() =>
        Array.from({ length: 30 }, () => ({
            left: `${Math.random() * 100}%`,
            delay: `${Math.random() * 1.0}s`,
            duration: `${0.9 + Math.random() * 0.8}s`,
            width: 0.5 + Math.random() * 1,
            height: 30 + Math.random() * 80,
            opacity: 0.15 + Math.random() * 0.35,
        })), []);

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: `radial-gradient(ellipse at center bottom, ${starColor}50 0%, ${starColor}15 30%, #000 70%)`,
            overflow: 'hidden',
            animation: 'xingye-depart-in 0.6s ease-out forwards',
            pointerEvents: 'auto',
        }}>
            {/* 星軌長條 */}
            {trails.map((t, i) => (
                <div key={`trail-${i}`} style={{
                    position: 'absolute',
                    left: t.left, bottom: '-10%',
                    width: `${t.width}px`, height: `${t.height}px`,
                    background: `linear-gradient(to top, transparent, rgba(255,255,255,${t.opacity}), transparent)`,
                    borderRadius: '1px',
                    animation: `xingye-trail-rise ${t.duration} ease-in ${t.delay} infinite`,
                }} />
            ))}
            {/* 星點粒子 */}
            {particles.map((p, i) => (
                <div key={i} style={{
                    position: 'absolute',
                    left: p.left, top: p.top,
                    width: `${p.size}px`, height: `${p.size}px`,
                    background: '#fff', borderRadius: '50%',
                    boxShadow: `0 0 ${p.size * 3}px rgba(255,255,255,0.8)`,
                    animation: `xingye-starrise ${p.duration} linear ${p.delay} infinite`,
                }} />
            ))}
            {/* 中心文字 */}
            <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: '12px',
            }}>
                <p style={{
                    fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)',
                    letterSpacing: '0.35em',
                    fontFamily: '"Noto Serif TC", serif',
                    textShadow: `0 0 20px ${starColor}60, 0 0 40px ${starColor}30`,
                    animation: 'xingye-pulse 2s infinite',
                }}>
                    升空中…
                </p>
            </div>
            <style>{`
                @keyframes xingye-depart-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes xingye-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
                @keyframes xingye-starrise {
                    0% { transform: translateY(20vh) scale(0.5); opacity: 0; }
                    10% { opacity: 1; }
                    100% { transform: translateY(-120vh) scale(2); opacity: 0; }
                }
                @keyframes xingye-trail-rise {
                    0% { transform: translateY(0) scaleY(0.3); opacity: 0; }
                    15% { opacity: 1; }
                    100% { transform: translateY(-130vh) scaleY(1.5); opacity: 0; }
                }
            `}</style>
        </div>
    );
}

export function UI() {
    const isMobile = useGameStore((state) => state.isMobile);
    const isControlEnabled = useGameStore((state) => state.isControlEnabled);
    const fromStar = useGameStore((state) => state.fromStar);
    const starColor = useGameStore((state) => state.starColor) || '#ffffff';

    const [isDeparting, setIsDeparting] = useState(false);

    // ESC / 返回按鈕：觸發離開動畫
    const handleDepart = useCallback(() => {
        if (!fromStar || isDeparting) return;
        setIsDeparting(true);
        setTimeout(() => {
            window.location.href = `/logodrift/?returnTo=${encodeURIComponent(fromStar)}`;
        }, 1800);
    }, [fromStar, isDeparting]);

    useEffect(() => {
        if (!fromStar) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                handleDepart();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fromStar, handleDepart]);

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 10
        }}>
            <LoadingScreen />

            <div style={{
                position: 'absolute',
                top: 0, left: 0, width: '100%', height: '100%',
                pointerEvents: 'none',

                opacity: isControlEnabled ? 1 : 0,
                visibility: isControlEnabled ? 'visible' : 'hidden',
                transition: `opacity 0.5s ease, visibility 0s linear ${isControlEnabled ? '0s' : '0.5s'}`
            }}>
                <AudioButton />
                <SideBar onDepart={handleDepart} />

                {isMobile &&
                    <TouchJoystick
                        input={input}
                        actions={{
                            forward: 'MoveForward',
                            backward: 'MoveBackward',
                            left: 'RotateLeft',
                            right: 'RotateRight',
                            run: 'Run'
                        }} />}

                {fromStar && <ControlHints isMobile={isMobile} />}
            </div>

            {isDeparting && <DepartureOverlay starColor={starColor} />}
        </div>
    );
}